import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api, { safeIconSrc } from '../api/client';
import { CoinIcon, FloatIcon, ScreenTitle, StarIcon } from './CurrencyIcons';
import AlertModal from './AlertModal';
import { iconUrl } from '../utils/assets';
import { useLocale } from '../i18n/LocaleContext';
import { useGameAlert } from '../hooks/useGameAlert';
import { mergeClickerState, balanceWithPending } from '../utils/clickerMerge';
import { awaitDrainPendingTaps, getOutstandingTaps } from '../utils/tapFlush';
import { payWithStars, prefetchStarsInvoice, watchPaymentSettle } from '../utils/starsPay';

const BRANCHES = [
  { id: 'tap', icon: 'muscle.png', labelKey: 'branchTap' },
  { id: 'hour', icon: 'chart_up.png', labelKey: 'branchFarm' },
  { id: 'energy', icon: 'energy_new.png', labelKey: 'branchEnergy' },
];

const Upgrades = ({ clickerData, onClickerUpdate, onRefresh }) => {
  const { t, lang } = useLocale();
  const { alert, closeAlert, confirmAlert, showError, showInfo } = useGameAlert();
  const [items, setItems] = useState([]);
  const [synergy, setSynergy] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [branch, setBranch] = useState('tap');
  // Re-render while flush drains so balanceWithPending stays live across tabs
  const [pendingRev, setPendingRev] = useState(0);
  const settleStopRef = useRef(null);

  useEffect(() => () => settleStopRef.current?.(), []);

  const liveBalance = (() => {
    void pendingRev;
    return balanceWithPending(clickerData);
  })();

  useEffect(() => {
    if (getOutstandingTaps() <= 0) return undefined;
    const id = setInterval(() => {
      // Сначала выход по пустой очереди, потом отрисовка: иначе в свёрнутом
      // приложении таймер крутился бы вечно, пять раз в секунду перерисовывая
      // список, которого никто не видит.
      if (getOutstandingTaps() <= 0) {
        clearInterval(id);
        return;
      }
      if (document.visibilityState === 'visible') setPendingRev((n) => n + 1);
    }, 200);
    return () => clearInterval(id);
  }, [clickerData?.balance]);

  const mergeClicker = useCallback((synced, opts = {}) => {
    if (!synced) return;
    onClickerUpdate?.((prev) =>
      mergeClickerState(prev, synced, { preferRemoteBalance: true, ...opts })
    );
  }, [onClickerUpdate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const upgrades = await api.upgradesList();
      setItems(upgrades.items || []);
      setSynergy(upgrades.synergy || {});
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      const cat = item.category || item.branch;
      const eff = item.effect_per_level || {};
      if (branch === 'tap') {
        return cat === 'tap' || cat === 'premium' || Boolean(eff.coin_per_click);
      }
      if (branch === 'hour') {
        return cat === 'hour' || Boolean(eff.coin_per_hour);
      }
      if (branch === 'energy') {
        return cat === 'energy' || Boolean(eff.max_energy) || Boolean(eff.energy_rate);
      }
      return cat === branch;
    });
  }, [items, branch]);

  const buyCoins = async (id) => {
    setBusyId(id);
    try {
      const drained = await awaitDrainPendingTaps();
      if (drained.last) mergeClicker(drained.last);
      if (!drained.ok) {
        showError({
          message: lang === 'en' ? 'Still syncing taps — try again' : 'Тапы ещё синхронизируются — попробуй ещё раз',
        });
        return;
      }
      const result = await api.upgradesBuy(id);
      setItems(result.items || []);
      setSynergy(result.synergy || {});
      if (result.clicker) mergeClicker(result.clicker);
      // Force a server reconciliation so Main always sees the post-spend balance
      if (onRefresh) await onRefresh({ preferRemoteBalance: true, forceSync: true });
      else if (!result.clicker) mergeClicker(await api.clickerSync());
      // Notify another open desktop tab/window using the same account.
      try {
        localStorage.setItem('tapmars:balance-sync', String(Date.now()));
        window.dispatchEvent(new Event('tapmars:balance-sync'));
      } catch (_) {
        /* storage may be unavailable in restricted webviews */
      }
      try {
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      } catch (_) {
        /* ignore */
      }
    } catch (err) {
      showError(err);
    } finally {
      setBusyId(null);
    }
  };

  const buyStars = (item) => {
    const sku = `upgrade_${item.code}`;
    const onStatus = async (status) => {
      if (status === 'cancelled' || status === 'failed') {
        setBusyId(null);
        return;
      }
      if (status !== 'paid') return;
      try {
        const drained = await awaitDrainPendingTaps();
        if (drained.last) mergeClicker(drained.last);
        mergeClicker(await api.clickerSync());
        await load();
      } catch (err) {
        showError(err);
      } finally {
        setBusyId(null);
        settleStopRef.current?.();
        settleStopRef.current = null;
      }
    };

    settleStopRef.current?.();
    setBusyId(item.id);
    let keepBusy = false;
    payWithStars({ sku, onStatus })
      .then((result) => {
        if (result.reason === 'error') {
          showError(result.error);
          return;
        }
        if (result.chat_sent || result.needsPoll) {
          keepBusy = true;
          showInfo({
            icon: 'crown.png',
            title: t('starsLabel') || 'Stars',
            body:
              t('starsSentChat') ||
              (lang === 'en'
                ? 'Invoice sent to the bot chat — open @Tap_Mars_bot and pay there.'
                : 'Счёт отправлен в чат с ботом — открой @Tap_Mars_bot и оплати там.'),
            confirmLabel: t('gotIt'),
          });
          const stop = watchPaymentSettle(async () => {
            mergeClicker(await api.clickerSync());
            await load();
          });
          settleStopRef.current = () => {
            stop();
            setBusyId(null);
          };
          setTimeout(() => settleStopRef.current?.(), 45000);
          return;
        }
        if (result.needsRetap || result.reason === 'needs_retap') {
          showInfo({
            icon: 'crown.png',
            title: t('starsLabel') || 'Stars',
            body: t('starsTapAgain') || (lang === 'en' ? 'Tap again to open payment.' : 'Нажми ещё раз, чтобы открыть оплату.'),
            confirmLabel: t('gotIt'),
          });
          return;
        }
        if (!result.ok) {
          showInfo({ title: t('starsOnly'), icon: 'bulb.png' });
        }
      })
      .catch((err) => showError(err))
      .finally(() => {
        if (!keepBusy) setBusyId(null);
      });
  };

  const branchIcon = (item) => {
    if (item.icon) return safeIconSrc(item.icon);
    const cat = item.category || item.branch;
    if (cat === 'hour') return iconUrl('chart_up.png');
    if (cat === 'energy') return iconUrl('energy_new.png');
    return iconUrl('muscle.png');
  };

  const effectLine = (item) => {
    const eff = item.effect_per_level || {};
    const bits = [];
    if (eff.max_energy) bits.push(lang === 'en' ? `+${eff.max_energy} cap` : `+${eff.max_energy} запас`);
    if (eff.energy_rate) bits.push(lang === 'en' ? `+${eff.energy_rate}/s regen` : `+${eff.energy_rate}/с реген`);
    if (eff.coin_per_click) bits.push(lang === 'en' ? `+${eff.coin_per_click}/tap` : `+${eff.coin_per_click}/тап`);
    if (eff.coin_per_hour) bits.push(lang === 'en' ? `+${eff.coin_per_hour}/h` : `+${eff.coin_per_hour}/час`);
    return bits.join(' · ');
  };

  return (
    <main className="upgrades screen-enter">
      <ScreenTitle icon={iconUrl('toolbox.png')} title={t('buildTitle')} subtitle={t('buildSubtitle')} />
      {clickerData?.balance != null ? (
        <div className="build-balance soft-card">
          <CoinIcon size={22} />
          <span>{Number(liveBalance).toFixed(2)}</span>
        </div>
      ) : null}

      <div className="hub-tabs hub-tabs-3 build-tabs" role="tablist">
        {BRANCHES.map(({ id, icon, labelKey }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={branch === id}
            className={`hub-tab ${branch === id ? 'is-active' : ''}`}
            onClick={() => setBranch(id)}
          >
            <FloatIcon src={iconUrl(icon)} size={22} animated={branch === id} eager native />
            <span>{t(labelKey)}</span>
            <span className="branch-lvl">{synergy[id === 'hour' ? 'hour' : id] || 0}</span>
          </button>
        ))}
      </div>

      {branch === 'energy' ? (
        <p className="build-hint soft-card">{t('energyBuildHint')}</p>
      ) : null}

      {loading ? <div className="inline-message shimmer-line">{t('loading')}</div> : null}

      <div className="upgrades-list">
        {filtered.map((item) => {
          const isStars = Boolean(item.cost_stars) || item.premium;
          const effects = effectLine(item);
          return (
            <div className={`upgrades-item soft-card build-row ${isStars ? 'is-stars-row' : ''}`} key={item.id}>
              <div className="item-left">
                <FloatIcon src={branchIcon(item)} size={36} className="item-icon-fx" eager native animated />
                <div className="item-text">
                  <span className="upgrades-item-info-name">
                    {lang === 'en' ? item.name_en || item.name : item.name}
                    <span className="level-badge">
                      {t('levelShort')} {item.level}/{item.max_level}
                    </span>
                    {isStars ? (
                      <span className="level-badge stars-badge">
                        <StarIcon size={10} /> {t('starsLabel')}
                      </span>
                    ) : null}
                  </span>
                  <span className="item-desc">
                    {lang === 'en' ? item.description_en || item.description : item.description}
                  </span>
                  {effects ? <span className="build-effect-chip">{effects}</span> : null}
                </div>
              </div>
              <div className="item-actions">
                {item.maxed ? (
                  <span className="btn-disabled">{t('maxed')}</span>
                ) : isStars ? (
                  <button
                    type="button"
                    className="action-btn stars soft-btn"
                    disabled={busyId != null}
                    onPointerDown={() => prefetchStarsInvoice(`upgrade_${item.code}`)}
                    onClick={() => buyStars(item)}
                  >
                    <StarIcon size={14} />
                    {item.cost_stars}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="action-btn soft-btn"
                    disabled={
                      busyId != null ||
                      (clickerData?.balance != null && liveBalance < Number(item.cost_coins || 0))
                    }
                    onClick={() => buyCoins(item.id)}
                  >
                    <CoinIcon size={16} />
                    {item.cost_coins}
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {!loading && filtered.length === 0 ? (
          <div className="inline-message">{t('emptyBuild')}</div>
        ) : null}
      </div>

      <AlertModal
        open={Boolean(alert)}
        {...(alert || {})}
        onClose={closeAlert}
        onConfirm={alert?.mode === 'confirm' ? confirmAlert : closeAlert}
      />
    </main>
  );
};

export default Upgrades;
